import {
  DestinationEntityRecordStatus,
  IMigratedDataSummary,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type ISourceEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { PatientRelationship } from '@principle-theorem/principle-core';
import {
  PatientRelationshipType,
  type IBasePatient,
  type IHasPrimaryContact,
  type IPatient,
  type IsPrimaryContact,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  asDocRef,
  getError,
  isSameRef,
  multiMap,
  resolveSequentially,
  safeCombineLatest,
  toNamedDocument,
  toTimestamp,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { of, type Observable } from 'rxjs';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientRelationshipDestinationEntity,
  type IPatientRelationshipJobData as IPatientRelationshipBaseJobData,
} from '../../../destination/entities/patient-relationship';
import { type TranslationMapHandler } from '../../../translation-map';
import { D4WPatient, PatientSourceEntity } from '../../source/entities/patient';
import { PatientDestinationEntity } from './patients';
import {
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';

export const PATIENT_RELATIONSHIP_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'patientRelationships',
    label: 'Patient Relationships',
    description: '',
  },
});

export interface IPatientRelationshipDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  patientRefs: DocumentReference<IPatient>[];
}

export type IPatientRelationshipJobData =
  IPatientRelationshipBaseJobData<D4WPatient>;

export interface IPatientMigrationData {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  primaryContacts: (IHasPrimaryContact & {
    patientRef: DocumentReference<IPatient>;
  })[];
}

export class PatientRelationshipDestinationEntity extends BasePatientRelationshipDestinationEntity<
  D4WPatient,
  IPatientRelationshipDestinationRecord,
  IPatientMigrationData
> {
  destinationEntity = PATIENT_RELATIONSHIP_DESTINATION_ENTITY;

  patientSourceEntity = new PatientSourceEntity();
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  getMigratedData$(
    record: IDestinationEntityRecord<IPatientRelationshipDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return safeCombineLatest(
      record.data.patientRefs.map((patientRef) => Firestore.getDoc(patientRef))
    ).pipe(
      multiMap((patient) => ({
        label: 'Patient',
        data: patient,
      }))
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientRelationshipJobData
  ): Promise<
    | IPatientMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients
        .getSourceRecordId(data.sourcePatient.data)
        .toString();
      const sourcePatientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!sourcePatientRef) {
        throw new Error(
          `Patient relation can't be found for id ${sourcePatientId}`
        );
      }

      const relationshipRecords =
        await this.sourceEntities.patients.filterRecords(
          migration,
          'familyHeadId',
          sourcePatientId
        );

      const primaryContacts = compact(
        await resolveSequentially(relationshipRecords, async (relationship) => {
          const relationshipPatientId = this.sourceEntities.patients
            .getSourceRecordId(relationship.data)
            .toString();
          const relationshipPatientRef =
            await translationMap.getDestination<IPatient>(
              relationshipPatientId,
              PATIENT_RESOURCE_TYPE
            );

          if (!relationshipPatientRef) {
            throw new Error(
              `Patient relation can't be found for id ${relationshipPatientId}`
            );
          }

          if (isSameRef(sourcePatientRef, relationshipPatientRef)) {
            return;
          }

          return {
            patientRef: relationshipPatientRef,
            primaryContact: {
              patient: toNamedDocument(
                await Firestore.getDoc(
                  asDocRef<IBasePatient & IsPrimaryContact>(sourcePatientRef)
                )
              ),
              type: PatientRelationshipType.Unknown,
            },
          };
        })
      );

      return {
        sourcePatientId,
        patientRef: sourcePatientRef,
        primaryContacts,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMapHandler: TranslationMapHandler,
    jobData: IPatientRelationshipJobData,
    migrationData: IPatientMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const patientRefs = await resolveSequentially(
        migrationData.primaryContacts,
        async (relationship) => {
          await FirestoreMigrate.patchDoc(relationship.patientRef, {
            primaryContact: relationship.primaryContact,
          });

          await PatientRelationship.addPrimaryRelationship(
            await Firestore.getDoc(relationship.patientRef)
          );
          return relationship.patientRef;
        }
      );
      return this._buildSuccessResponse(jobData.sourcePatient.record, [
        migrationData.patientRef,
        ...patientRefs,
      ]);
    } catch (error) {
      return this._buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  private _buildSuccessResponse(
    sourcePatient: Pick<
      IGetRecordResponse<D4WPatient>['record'],
      'label' | 'uid' | 'id'
    >,
    patientRefs: DocumentReference<IPatient>[]
  ): IDestinationEntityRecord<IPatientRelationshipDestinationRecord> {
    return {
      uid: sourcePatient.uid,
      label: sourcePatient.label,
      data: {
        sourceRef: sourcePatient.id,
        patientRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.id,
      migratedAt: toTimestamp(),
    };
  }
}
